/**
 * Geometry hit-testing for the imported DXF floor plan.
 *
 * The floor plan is rendered as a single non-interactive Konva.Shape for
 * performance, so Konva's built-in event hit-graph can't tell us which polyline
 * the pointer is over. Instead we do cheap CPU picking: bounding-box cull every
 * polyline, then measure point-to-segment distance only for the survivors.
 */

export type FitBounds = { minX: number; minY: number; maxX: number; maxY: number };

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * Shrink floor-plan AABB by dropping outlier polylines (same framing as Canvas
 * DXF auto-fit). Prevents Load Layout from zooming out to distant noise.
 */
export function getRobustFitBounds(
  fallback: FitBounds | null,
  bboxes: ArrayLike<number>
): FitBounds | null {
  if (!fallback) return null;
  if (!bboxes || bboxes.length < 16) return fallback;

  const rawW = Math.max(fallback.maxX - fallback.minX, 1);
  const rawH = Math.max(fallback.maxY - fallback.minY, 1);
  const count = Math.floor(bboxes.length / 4);

  const centersX: number[] = [];
  const centersY: number[] = [];
  for (let i = 0; i < count; i++) {
    const o = i * 4;
    centersX.push((bboxes[o] + bboxes[o + 2]) / 2);
    centersY.push((bboxes[o + 1] + bboxes[o + 3]) / 2);
  }

  const cx = median(centersX);
  const cy = median(centersY);
  const devX = centersX.map((x) => Math.abs(x - cx));
  const devY = centersY.map((y) => Math.abs(y - cy));

  const thresholdX = Math.max(median(devX) * 6, rawW * 0.08);
  const thresholdY = Math.max(median(devY) * 6, rawH * 0.08);

  let kept = 0;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < count; i++) {
    if (Math.abs(centersX[i] - cx) > thresholdX || Math.abs(centersY[i] - cy) > thresholdY) {
      continue;
    }
    const o = i * 4;
    minX = Math.min(minX, bboxes[o]);
    minY = Math.min(minY, bboxes[o + 1]);
    maxX = Math.max(maxX, bboxes[o + 2]);
    maxY = Math.max(maxY, bboxes[o + 3]);
    kept += 1;
  }

  const minKept = Math.max(20, Math.floor(count * 0.1));
  if (kept < minKept || !Number.isFinite(minX) || !Number.isFinite(minY)) {
    return fallback;
  }

  const keptW = Math.max(maxX - minX, 1);
  const keptH = Math.max(maxY - minY, 1);
  if (keptW < rawW * 0.15 && keptH < rawH * 0.15) {
    return fallback;
  }

  return { minX, minY, maxX, maxY };
}

function percentileSorted(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.round(p * (sorted.length - 1)))
  );
  return sorted[idx];
}

/**
 * Tighter DXF framing for 3D: drop the outer `trimFrac` of polyline centers on
 * each axis so sparse outliers don't keep the camera zoomed out.
 */
export function getDenseFitBounds(
  fallback: FitBounds | null,
  bboxes: ArrayLike<number>,
  trimFrac = 0.05
): FitBounds | null {
  if (!fallback) return null;
  if (!bboxes || bboxes.length < 16) return fallback;

  const count = Math.floor(bboxes.length / 4);
  if (count < 8) return fallback;

  const centersX: number[] = [];
  const centersY: number[] = [];
  for (let i = 0; i < count; i++) {
    const o = i * 4;
    centersX.push((bboxes[o] + bboxes[o + 2]) / 2);
    centersY.push((bboxes[o + 1] + bboxes[o + 3]) / 2);
  }

  const sortedX = [...centersX].sort((a, b) => a - b);
  const sortedY = [...centersY].sort((a, b) => a - b);
  const lo = Math.min(Math.max(trimFrac, 0), 0.2);
  const hi = 1 - lo;
  const minCX = percentileSorted(sortedX, lo);
  const maxCX = percentileSorted(sortedX, hi);
  const minCY = percentileSorted(sortedY, lo);
  const maxCY = percentileSorted(sortedY, hi);

  let kept = 0;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < count; i++) {
    if (
      centersX[i] < minCX ||
      centersX[i] > maxCX ||
      centersY[i] < minCY ||
      centersY[i] > maxCY
    ) {
      continue;
    }
    const o = i * 4;
    minX = Math.min(minX, bboxes[o]);
    minY = Math.min(minY, bboxes[o + 1]);
    maxX = Math.max(maxX, bboxes[o + 2]);
    maxY = Math.max(maxY, bboxes[o + 3]);
    kept += 1;
  }

  const minKept = Math.max(12, Math.floor(count * 0.2));
  if (kept < minKept || !Number.isFinite(minX) || !Number.isFinite(minY)) {
    return getRobustFitBounds(fallback, bboxes) ?? fallback;
  }

  const rawW = Math.max(fallback.maxX - fallback.minX, 1);
  const rawH = Math.max(fallback.maxY - fallback.minY, 1);
  const keptW = Math.max(maxX - minX, 1);
  const keptH = Math.max(maxY - minY, 1);
  // If trimming collapsed to a tiny island, fall back to robust / full bounds.
  if (keptW < rawW * 0.12 && keptH < rawH * 0.12) {
    return getRobustFitBounds(fallback, bboxes) ?? fallback;
  }

  return { minX, minY, maxX, maxY };
}

/** Precompute a flat [minX, minY, maxX, maxY] bbox per polyline. */
export function computePolylineBBoxes(polylines: number[][]): Float64Array {
  const out = new Float64Array(polylines.length * 4);
  for (let p = 0; p < polylines.length; p++) {
    const pts = polylines[p];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i + 1 < pts.length; i += 2) {
      const x = pts[i];
      const y = pts[i + 1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const o = p * 4;
    out[o] = minX;
    out[o + 1] = minY;
    out[o + 2] = maxX;
    out[o + 3] = maxY;
  }
  return out;
}

/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
function segDistSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey;
}

/**
 * Returns the index of the closest polyline within `threshold` world units of
 * (wx, wy), or -1 if none. Hidden layers are skipped.
 */
export function pickPolyline(
  wx: number,
  wy: number,
  threshold: number,
  polylines: number[][],
  bboxes: Float64Array,
  polylineLayers: number[],
  layers: { visible: boolean }[]
): number {
  const thr = threshold;
  const thrSq = thr * thr;
  let best = -1;
  let bestSq = thrSq;

  for (let p = 0; p < polylines.length; p++) {
    const o = p * 4;
    // Bounding-box reject (expanded by the pick threshold).
    if (
      wx < bboxes[o] - thr ||
      wx > bboxes[o + 2] + thr ||
      wy < bboxes[o + 1] - thr ||
      wy > bboxes[o + 3] + thr
    ) {
      continue;
    }

    const li = polylineLayers[p];
    const layer = layers[li];
    if (layer && layer.visible === false) continue;

    const pts = polylines[p];
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const d = segDistSq(wx, wy, pts[i], pts[i + 1], pts[i + 2], pts[i + 3]);
      if (d < bestSq) {
        bestSq = d;
        best = p;
      }
    }
  }

  return best;
}
